/**
 * Same-day cache for Hunt Forecast top-picks.
 * Cache is keyed by source + limit so prefetch and page requests align.
 */
import { safeStorage } from './safeStorage';

const CACHE_KEY_PREFIX = 'yona_top_picks_';
const DEFAULT_LIMIT = 6;
const DEFAULT_SOURCE = 'club';

/** @type {Map<string, Promise<import('axios').AxiosResponse>>} */
const inFlightTopPicks = new Map();

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function normalizeSource(source) {
  return source === 'personal' ? 'personal' : 'club';
}

function normalizeLimit(limit) {
  const n = Number(limit);
  if (!Number.isFinite(n)) return DEFAULT_LIMIT;
  return Math.max(1, Math.min(6, Math.round(n)));
}

function getStorageKey({ source = DEFAULT_SOURCE, limit = DEFAULT_LIMIT } = {}) {
  const src = normalizeSource(source);
  const lim = normalizeLimit(limit);
  return `${CACHE_KEY_PREFIX}${getTodayKey()}:${src}:${lim}`;
}

/**
 * Build the top-picks API path used by Hunt Forecast.
 * @param {{ source?: 'club'|'personal', limit?: number }} options
 */
export function buildTopPicksUrl({ source = DEFAULT_SOURCE, limit = DEFAULT_LIMIT } = {}) {
  const src = normalizeSource(source);
  const lim = normalizeLimit(limit);
  const includePersonal = src === 'personal' ? 1 : 0;
  const includeClub = src === 'club' ? 1 : 0;
  return `/hunt-forecast/top-picks/?limit=${lim}&include_personal=${includePersonal}&include_club=${includeClub}`;
}

/**
 * Return cached top-picks for today matching source/limit.
 * @param {{ source?: 'club'|'personal', limit?: number }} options
 * @returns {{ data: object } | null}
 */
export function getCachedTopPicks({ source = DEFAULT_SOURCE, limit = DEFAULT_LIMIT } = {}) {
  const key = getStorageKey({ source, limit });
  const raw = safeStorage.getJSON(key);
  if (!raw || !raw.data || !Array.isArray(raw.data.picks)) return null;
  return { data: raw.data };
}

/**
 * Store top-picks response for today (source/limit specific).
 * @param {object} data
 * @param {{ source?: 'club'|'personal', limit?: number }} options
 */
export function setCachedTopPicks(data, { source = DEFAULT_SOURCE, limit = DEFAULT_LIMIT } = {}) {
  const key = getStorageKey({ source, limit });
  safeStorage.setJSON(key, { date: getTodayKey(), source: normalizeSource(source), limit: normalizeLimit(limit), data });
}

/**
 * Fetch top-picks with in-flight deduplication (same URL = one network call).
 * @param {object} api
 * @param {{ source?: 'club'|'personal', limit?: number }} options
 */
export function fetchTopPicks(api, { source = DEFAULT_SOURCE, limit = DEFAULT_LIMIT } = {}) {
  if (!api || typeof api.get !== 'function') {
    return Promise.reject(new Error('api client required'));
  }
  const url = buildTopPicksUrl({ source, limit });
  const existing = inFlightTopPicks.get(url);
  if (existing) return existing;

  const promise = api.get(url)
    .then((resp) => {
      if (resp?.data) setCachedTopPicks(resp.data, { source, limit });
      return resp;
    })
    .finally(() => {
      inFlightTopPicks.delete(url);
    });

  inFlightTopPicks.set(url, promise);
  return promise;
}

/**
 * Prefetch Hunt Forecast top-picks (club stands, limit 6 — matches default page).
 * Fire-and-forget; dedupes if the page is already fetching the same URL.
 */
export function prefetchTopPicks(api, { source = DEFAULT_SOURCE, limit = DEFAULT_LIMIT } = {}) {
  fetchTopPicks(api, { source, limit }).catch(() => {});
}
